import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import useBrains from '@/hooks/useBrains';
import useDesks from '@/hooks/useDesks';
import useMembers from '@/hooks/useMembers';
import { selectAvailableChannels } from '@/redux/integrations/selectors';
import type { OptionBase } from '@/shared/types/common';
import type { IntegrationType } from '@/shared/types/integration';
import type { Member } from '@/shared/types/member';
import { getChannelKey } from '@/util/channelName';

export type FilterColumnOption = OptionBase;

export type FilterColumnOptionsResult = {
  options: FilterColumnOption[] | null; // null = no enum source; fall back to free-text
  isLoading: boolean;
};

// Columns whose values are well-known account-scoped entities. The picker uses
// these to render a checkbox list of real labels rather than a free-text input.
const ENUM_COLUMNS = new Set([
  'brain_parent_id',
  'desk_id',
  'channel',
  'goal_achieved',
  'user_sentiment',
  'agent_id',
]);

export const isEnumBackedColumn = (column: string): boolean => ENUM_COLUMNS.has(column);

export const memberLabel = (member: Member): string =>
  member.name || member.email || member.user_id;

// Static mapping mirrors the analytics GoalAchieved proto enum
// (dialog-service/dialog/src/dialog/proto/dialog.proto): 0=UNSPECIFIED,
// 1=SUCCESS, 2=PARTIAL, 3=FAIL. '0' rows are pre-judge / "judge didn't
// classify" — same semantics as NULL, so we render them as "Not classified".
// Filter picker only exposes 1/2/3; '0' is for display-side resolution only.
const GOAL_ACHIEVED_VALUES = ['3', '2', '1'] as const;
export type GoalAchievedValue = '0' | (typeof GOAL_ACHIEVED_VALUES)[number];
export const GOAL_ACHIEVED_LABEL_KEYS: Record<GoalAchievedValue, string> = {
  '0': 'analytics.not_classified',
  '3': 'analytics.goal_not_achieved',
  '2': 'analytics.goal_partially_achieved',
  '1': 'analytics.goal_achieved',
};

// Mirrors the analytics UserSentiment proto enum: 0=UNSPECIFIED, 1=POSITIVE,
// 2=NEUTRAL, 3=NEGATIVE. '0' is treated the same as NULL for display ("Not
// classified") — the filter picker still only exposes 1/2/3.
const USER_SENTIMENT_VALUES = ['3', '2', '1'] as const;
export type UserSentimentValue = '0' | (typeof USER_SENTIMENT_VALUES)[number];
export const USER_SENTIMENT_LABEL_KEYS: Record<UserSentimentValue, string> = {
  '0': 'analytics.not_classified',
  '3': 'analytics.negative',
  '2': 'analytics.neutral',
  '1': 'analytics.positive',
};

// Returns the list of pickable values for known account-scoped filter axes:
// AI agents, environments (desks), and channels. For any other column it
// returns `options: null` so the caller knows to fall back to a free-text
// editor. The three underlying hooks are always invoked (Rules of Hooks); the
// switch happens at the return statement.
export const useFilterColumnOptions = (column: string | null): FilterColumnOptionsResult => {
  const { t } = useTranslation();
  const { brains, listStatus: brainsStatus } = useBrains();
  const { desks, listStatus: desksStatus } = useDesks();
  const { members, listStatus: membersStatus } = useMembers();
  const channels = useSelector(selectAvailableChannels);

  return useMemo<FilterColumnOptionsResult>(() => {
    if (column === 'brain_parent_id') {
      const options: FilterColumnOption[] = (brains ?? []).map((b) => ({
        value: b.brain_parent_id ?? b.brain_id,
        label: b.name,
      }));
      return { options, isLoading: brainsStatus === 'pending' };
    }
    if (column === 'desk_id') {
      const options: FilterColumnOption[] = (desks ?? []).map((d) => ({
        value: d.desk_id,
        label: d.name,
      }));
      return { options, isLoading: desksStatus === 'pending' };
    }
    if (column === 'agent_id') {
      const options: FilterColumnOption[] = (members ?? []).map((m) => ({
        value: m.user_id,
        label: memberLabel(m),
      }));
      return { options, isLoading: membersStatus === 'pending' };
    }
    if (column === 'channel') {
      const options: FilterColumnOption[] = channels.map((c: IntegrationType) => ({
        value: c,
        label: t(getChannelKey(c)),
      }));
      return { options, isLoading: false };
    }
    if (column === 'goal_achieved') {
      const options: FilterColumnOption[] = GOAL_ACHIEVED_VALUES.map((value) => ({
        value,
        label: t(GOAL_ACHIEVED_LABEL_KEYS[value]),
      }));
      return { options, isLoading: false };
    }
    if (column === 'user_sentiment') {
      const options: FilterColumnOption[] = USER_SENTIMENT_VALUES.map((value) => ({
        value,
        label: t(USER_SENTIMENT_LABEL_KEYS[value]),
      }));
      return { options, isLoading: false };
    }
    return { options: null, isLoading: false };
  }, [column, brains, brainsStatus, desks, desksStatus, members, membersStatus, channels, t]);
};
